import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  formatScopeLabel,
  VoucherScopeInput,
  VoucherScopeMode,
  VoucherScopeType,
} from '@/client/types/voucher';

interface VoucherScopesEditorProps {
  value: VoucherScopeInput[];
  onChange: (scopes: VoucherScopeInput[]) => void;
  disabled?: boolean;
  /** Compact helper text for create vs edit contexts. */
  hint?: string;
}

const TYPE_OPTIONS: { value: VoucherScopeType; label: string; refLabel: string }[] = [
  { value: 'object', label: 'Object (toid)', refLabel: 'toid' },
  { value: 'entity', label: 'Listing (eid)', refLabel: 'eid' },
  { value: 'entity_type', label: 'Category (etid)', refLabel: 'etid' },
];

const MODE_OPTIONS: { value: VoucherScopeMode; label: string }[] = [
  { value: 'descendants', label: 'Descendants (inclusive)' },
  { value: 'children', label: 'Children' },
  { value: 'self', label: 'Self only' },
  { value: 'exact', label: 'Exact' },
];

const VoucherScopesEditor: React.FC<VoucherScopesEditorProps> = ({
  value,
  onChange,
  disabled,
  hint = 'Empty = unrestricted (applies to everything). Rules are OR’d — any match qualifies.',
}) => {
  const [draftType, setDraftType] = useState<VoucherScopeType>('entity');
  const [draftRef, setDraftRef] = useState('');
  const [draftMode, setDraftMode] = useState<VoucherScopeMode>('descendants');

  const addScope = () => {
    const ref_id = Number(draftRef);
    if (!Number.isFinite(ref_id) || ref_id <= 0) return;

    const next: VoucherScopeInput = {
      scope_type: draftType,
      ref_id,
    };
    if (draftType === 'entity_type') {
      next.mode = draftMode;
    } else {
      next.mode = 'exact';
    }

    // Avoid exact duplicates
    const exists = value.some(
      (s) =>
        s.scope_type === next.scope_type &&
        s.ref_id === next.ref_id &&
        (s.mode || 'exact') === (next.mode || 'exact'),
    );
    if (exists) return;

    onChange([...value, next]);
    setDraftRef('');
  };

  const removeAt = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const refLabel =
    TYPE_OPTIONS.find((t) => t.value === draftType)?.refLabel ?? 'ref_id';

  return (
    <div className="space-y-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Scopes</p>
        <p className="mt-0.5 text-xs text-gray-500">{hint}</p>
      </div>

      {value.length === 0 ? (
        <p className="rounded-md border border-dashed border-gray-200 bg-white px-3 py-2 text-sm text-gray-500">
          Unrestricted — no targeting rules
        </p>
      ) : (
        <ul className="space-y-1.5">
          {value.map((scope, index) => (
            <li
              key={`${scope.scope_type}-${scope.ref_id}-${scope.mode ?? 'exact'}-${index}`}
              className="flex items-center justify-between gap-2 rounded-md border border-gray-100 bg-white px-3 py-2 text-sm"
            >
              <span className="font-medium text-gray-800">{formatScopeLabel(scope)}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => removeAt(index)}
                aria-label="Remove scope"
              >
                <Trash2 className="h-4 w-4 text-gray-500" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
        <div className="space-y-1 sm:col-span-1">
          <Label className="text-xs">Type</Label>
          <select
            value={draftType}
            disabled={disabled}
            onChange={(e) => setDraftType(e.target.value as VoucherScopeType)}
            className="w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm"
          >
            {TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1 sm:col-span-1">
          <Label className="text-xs">{refLabel}</Label>
          <Input
            type="number"
            min={1}
            value={draftRef}
            disabled={disabled}
            onChange={(e) => setDraftRef(e.target.value)}
            placeholder={`e.g. 5123`}
          />
        </div>
        {draftType === 'entity_type' ? (
          <div className="space-y-1 sm:col-span-1">
            <Label className="text-xs">Mode</Label>
            <select
              value={draftMode}
              disabled={disabled}
              onChange={(e) => setDraftMode(e.target.value as VoucherScopeMode)}
              className="w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm"
            >
              {MODE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="hidden sm:block" />
        )}
        <div className="flex items-end">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={disabled || !draftRef}
            onClick={addScope}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Add
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VoucherScopesEditor;
